// Retailer purchase orders and cross dock schedules
// (docs/retailer-po-and-cross-dock-plan.md). Backend: HHNodeServer
// routes/nurseryRetail.js.

// ─── Cross dock schedules ───

/** One store on one pick day. day_of_week is 0 = Sunday .. 6 = Saturday. */
export interface XdockScheduleRow {
  day_of_week: number;
  store_code: string;
  store_name: string | null;
  del_seq: string | null;
  route: string | null;
}

export interface XdockScheduleLine extends XdockScheduleRow {
  /** The customer that carries this store number, if we supply the store. */
  customer_name: string | null;
}

export interface XdockSchedule {
  id: number;
  name: string;
  /** Both null on the standing default, which answers for any date no dated schedule covers. */
  commence_date: string | null;
  end_date: string | null;
  is_default: boolean;
  active: boolean;
  /** The "updated 31/8/2026" stamp printed on the sheet. */
  updated_note: string | null;
  source_file: string | null;
  sheet_name: string | null;
  created_at: string;
  created_by_name: string | null;
  line_count: number;
  store_count: number;
  // Detail only
  lines?: XdockScheduleLine[];
}

/** One worksheet of an uploaded pick list, read and not yet saved. */
export interface XdockSheetPreview {
  sheet_name: string | null;
  meta: {
    name: string;
    commence_date: string | null;
    updated_note: string;
    day_columns: number;
  };
  rows: XdockScheduleRow[];
  summary: { rows: number; stores: number; per_day: number[] };
  /** Stores on the sheet with and without a customer. A nursery does not supply every store. */
  stores: { matched: number; unmatched: { store_code: string; store_name: string }[] };
}

export interface XdockFilePreview {
  file_name: string;
  sheets: XdockSheetPreview[];
}

export interface SaveXdockSchedulePayload {
  name: string;
  commence_date?: string | null;
  end_date?: string | null;
  is_default: boolean;
  updated_note?: string | null;
  file_name?: string | null;
  sheet_name?: string | null;
  rows: XdockScheduleRow[];
}

export type XdockNoAnswer = 'no_schedule' | 'not_covered' | 'store_not_listed' | 'no_pick_day' | 'bad_date' | 'no_store';

/** The next day a store's order can be made up, or why there is none. */
export type XdockPickAnswer =
  | {
      ok: true;
      date: string;
      day_name: string;
      del_seq: string | null;
      route: string | null;
      schedule_id: number;
      schedule_name: string;
      moved_days: number;
      /** Why the search started later than asked: today's cut-off has passed, or the date has gone. */
      held_by?: 'cutoff' | 'past';
      earliest_date?: string;
      cutoff?: string;
    }
  | { ok: false; reason: XdockNoAnswer; message: string };

/** The organisation's same-day cut-off for cross dock purchase orders. */
export interface XdockSettings {
  /** Minutes past local midnight; 480 is 8:00 am. */
  same_day_cutoff_minutes: number;
  cutoff_label: string;
  /** No setting saved: the 8:00 am default. */
  is_default: boolean;
}

export interface StorePickDays {
  store_code: string | null;
  pick_days: {
    schedule_id: number;
    schedule_name: string;
    days: { day_of_week: number; day_name: string; del_seq: string | null; route: string | null }[];
  } | null;
}

/** An open order a schedule change has left on a day its store is no longer picked. */
export interface XdockStaleOrder {
  order_id: number;
  order_number: string;
  customer_po: string | null;
  customer_name: string;
  store_code: string;
  total_trees: number;
  requested_delivery_date: string;
  current_date: string | null;
  current_route: string | null;
  new_date: string | null;
  new_route: string | null;
  new_schedule_name: string | null;
  /** Why no new day could be worked out. */
  reason: string | null;
  load_id: number | null;
  load_number: string | null;
  movable: boolean;
}

export interface XdockMoveResult {
  order_id: number;
  order_number: string;
  moved: boolean;
  new_date?: string;
  load_number?: string | null;
  message?: string;
}

// ─── Purchase order import ───

/** A file as it is sent for preview and again for confirm, with the operator's choices. */
export interface PoImportFile {
  name: string;
  text: string;
  customer_id?: number | null;
  despatch_farm_id?: number | null;
  pick_date?: string | null;
}

export type PoLineIssue =
  | 'unmatched' | 'inactive' | 'no_price' | 'price_mismatch'
  | 'over_stock' | 'pot_size' | 'no_quantity' | 'barcode_damaged';

export interface PoImportLine {
  line_number: number | null;
  barcode: string | null;
  item_number: string | null;
  description: string;
  comment: string | null;
  quantity: number;
  /** The unit price on the purchase order. */
  po_unit_price: number | null;
  match: {
    kind: 'product' | 'stock_item';
    id: number;
    name: string;
    tree_type_name: string | null;
    via: 'barcode' | 'remembered_barcode' | 'item_number';
  } | null;
  /** Our price for this customer, after its tier discount. */
  our_unit_price: number | null;
  price_source: string | null;
  /** Free trees at the despatch farm (everywhere when no farm is set). */
  free: number | null;
  issues: PoLineIssue[];
}

export interface PoImportNotice {
  code: string;
  message: string;
}

export interface PoImportCustomer {
  id: number;
  name: string;
  customer_code: string | null;
  parent_name: string | null;
}

/** One purchase order file, read and judged. */
export interface PoImportResult {
  file_name: string | null;
  file_hash: string;
  parsed: boolean;
  /** Nothing holds it back: confirm will make an order from it. */
  ready: boolean;
  /** Each of these holds the purchase order back until it is sorted. */
  problems: PoImportNotice[];
  warnings: PoImportNotice[];
  po?: {
    po_number: string;
    po_kind: 'stock' | 'trade' | 'customer';
    store_code: string | null;
    store_name: string | null;
    deliver_on: string | null;
    order_date: string | null;
    total_value: number | null;
    ship_from_code: string | null;
  };
  customer?: PoImportCustomer | null;
  /** More than one customer carries the store number; the operator chooses. */
  customer_candidates?: PoImportCustomer[];
  existing_order?: { id: number; order_number: string; status: string } | null;
  pick?: (XdockPickAnswer & { overridden: boolean }) | { ok: true; date: string; day_name: string; overridden: true } | null;
  /** The day the order is made up: the pick day, else the date on the purchase order. */
  despatch_date?: string | null;
  despatch_farm?: { id: number; name: string } | null;
  /** The cross dock load it joins. null: it goes on no load. id null: the load is made on confirm. */
  load?: { id: number | null; load_number: string | null; status: string | null; joins: boolean } | null;
  discount_pct?: number;
  our_total?: number;
  lines?: PoImportLine[];
  // After confirm
  created?: boolean;
  order?: { id: number; order_number: string };
}

export interface PoImportPreview {
  files: PoImportResult[];
  farms: { id: number; name: string }[];
  summary: { files: number; ready: number; held: number };
}

export interface PoImportConfirmed {
  files: PoImportResult[];
  summary: { files: number; created: number; held: number };
}

export interface RememberMatchPayload {
  barcode?: string | null;
  item_number?: string | null;
  description?: string | null;
  product_id?: number;
  stock_item_id?: number;
}

export const XDOCK_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

// ─── Range upload: the trees Bunnings stores can order now ───

export type RangeExclusion = 'inactive' | 'not_for_sale' | 'no_barcode' | 'bad_barcode' | 'no_pot_size' | 'below_minimum' | 'duplicate_barcode';

export interface RetailerRangeRow {
  kind: 'product' | 'stock_item';
  id: number;
  name: string;
  tree_type_name: string | null;
  barcode: string | null;
  /** What the file prints: Bunnings' own wording from a purchase order, else ours. */
  description: string;
  description_source: 'retailer' | 'ours';
  /** "200MM", "33L", or null when the tree type does not say. */
  pot_size: string | null;
  /** Free ready trees at the farm: ready lots less what orders already hold. */
  free: number;
  included: boolean;
  reasons: RangeExclusion[];
}

export interface RetailerRange {
  customer: { id: number; name: string; is_site: boolean };
  farm: { id: number; name: string };
  min_free: number;
  price_lists: { id: number; name: string }[];
  rows: RetailerRangeRow[];
  summary: { priced: number; included: number };
}

export interface RetailerRangeQuery {
  customer_id: number;
  farm_id: number;
  min_free: number;
  supplier_range?: string;
}
